import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, ChevronRight, Loader2, RefreshCcw, Sparkles, XCircle } from 'lucide-react'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { supabase } from '../../lib/supabaseClient'
import { cn } from '../../lib/utils'

// "Quiz me on this article" card. Owner-only on the news article
// page (PR G). Calls english-article-drill which generates and caches
// 3-5 multiple-choice questions per article. State machine:
//   idle    → "Quiz me" button
//   loading → spinner while the function fans out
//   active  → quiz UI (one question at a time, choices, reveal)
//   done    → score + "Try a fresh set" (force=true bypasses cache)
//   error   → message + retry
export default function EnglishArticleDrill({ articleId }) {
  const { t } = useTranslation()
  const [phase, setPhase] = useState('idle')
  const [drill, setDrill] = useState(null)
  const [index, setIndex] = useState(0)
  const [picks, setPicks] = useState([])
  const [revealed, setRevealed] = useState(false)
  const [error, setError] = useState(null)

  const start = useCallback(async ({ force = false } = {}) => {
    if (!articleId) return
    setPhase('loading')
    setError(null)
    setIndex(0)
    setPicks([])
    setRevealed(false)
    try {
      const { data, error: err } = await supabase.functions.invoke('english-article-drill', {
        body: { article_id: articleId, force },
      })
      if (err) throw new Error(err.message || 'failed')
      if (data?.error) throw new Error(data.error)
      if (!data?.drill || !Array.isArray(data.drill.questions) || data.drill.questions.length === 0) {
        throw new Error('empty drill')
      }
      setDrill(data.drill)
      setPhase('active')
    } catch (e) {
      setError(e?.message || 'failed')
      setPhase('error')
    }
  }, [articleId])

  const pick = useCallback((choiceIndex) => {
    if (revealed) return
    setPicks((prev) => {
      const next = prev.slice()
      next[index] = choiceIndex
      return next
    })
    setRevealed(true)
  }, [index, revealed])

  const next = useCallback(() => {
    setRevealed(false)
    if (!drill) return
    if (index + 1 >= drill.questions.length) {
      setPhase('done')
      return
    }
    setIndex(index + 1)
  }, [drill, index])

  if (phase === 'idle') {
    return (
      <Card className="border-app-primary-500/30 bg-app-primary-500/5">
        <CardContent className="p-3 sm:p-4 flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-app-primary-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground" dir="auto">
              {t('english.drill.cta')}
            </p>
            <p className="text-[11px] text-muted-foreground" dir="auto">
              {t('english.drill.ctaHint')}
            </p>
          </div>
          <Button size="sm" onClick={() => start({})}>
            {t('english.drill.start')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (phase === 'loading') {
    return (
      <Card>
        <CardContent className="p-3 sm:p-4 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-app-primary-500" />
          <span className="text-sm text-muted-foreground" dir="auto">{t('english.drill.loading')}</span>
        </CardContent>
      </Card>
    )
  }

  if (phase === 'error') {
    return (
      <Card>
        <CardContent className="p-3 sm:p-4 space-y-2">
          <p className="text-sm text-amber-700 dark:text-amber-400" dir="auto">{t('english.drill.error')}</p>
          {error && <p className="text-[10px] font-mono text-muted-foreground break-all" dir="ltr">{error}</p>}
          <Button size="sm" variant="outline" onClick={() => start({})}>
            <RefreshCcw className="w-3.5 h-3.5 me-1" />
            {t('english.drill.retry')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (phase === 'done' && drill) {
    const score = picks.reduce((acc, choice, i) => acc + (choice === drill.questions[i]?.correct_index ? 1 : 0), 0)
    return (
      <Card className="border-emerald-500/30 bg-emerald-500/5">
        <CardContent className="p-3 sm:p-4 space-y-3 text-center">
          <div className="inline-flex p-2.5 rounded-full bg-emerald-500/15 text-emerald-600">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <h3 className="text-base font-bold text-foreground" dir="auto">
            {t('english.drill.doneTitle')}
          </h3>
          <p className="text-sm text-foreground" dir="auto">
            {t('english.drill.score', { correct: score, total: drill.questions.length })}
          </p>
          <div className="flex gap-2 justify-center pt-1">
            <Button size="sm" variant="outline" onClick={() => start({ force: true })}>
              <RefreshCcw className="w-3.5 h-3.5 me-1" />
              {t('english.drill.fresh')}
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // phase === 'active'
  if (!drill) return null
  const q = drill.questions[index]
  if (!q) return null
  const pickedIndex = picks[index]
  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] uppercase tracking-wide font-semibold text-app-primary-500" dir="auto">
            {t('english.drill.progress', { current: index + 1, total: drill.questions.length })}
          </span>
        </div>
        <p className="text-sm font-semibold text-foreground leading-relaxed" dir="auto">
          {q.question}
        </p>
        <div className="space-y-1.5">
          {q.choices.map((c, i) => {
            const isCorrect = i === q.correct_index
            const isPicked = pickedIndex === i
            return (
              <button
                key={i}
                type="button"
                onClick={() => pick(i)}
                disabled={revealed}
                className={cn(
                  'w-full text-start px-3 py-2 rounded-md border text-sm transition-colors flex items-center gap-2',
                  revealed && isCorrect && 'border-emerald-500/50 bg-emerald-500/10 text-foreground',
                  revealed && !isCorrect && isPicked && 'border-red-500/50 bg-red-500/10 text-foreground',
                  revealed && !isCorrect && !isPicked && 'border-input text-muted-foreground',
                  !revealed && 'border-input hover:border-app-primary-500/40 hover:bg-accent',
                )}
                dir="auto"
              >
                {revealed && isCorrect && <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                {revealed && !isCorrect && isPicked && <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                <span className="flex-1">{c}</span>
              </button>
            )
          })}
        </div>
        {revealed && (
          <div className="space-y-1.5 text-xs border-t border-border pt-2">
            {q.explanation_en && <p className="text-foreground leading-relaxed" dir="ltr">{q.explanation_en}</p>}
            {q.explanation_ar && <p className="text-foreground leading-relaxed" dir="rtl">{q.explanation_ar}</p>}
          </div>
        )}
        {revealed && (
          <Button size="sm" onClick={next} className="w-full">
            {index + 1 >= drill.questions.length ? t('english.drill.finish') : t('english.drill.nextQuestion')}
            <ChevronRight className="w-4 h-4 ms-1 rtl:-scale-x-100" />
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
